import {AiTextPromptBoxError} from '@Libraries/ai-text-prompt-box-library';
import {AiTextErrorTypes} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type {Editor} from 'grapesjs';
import {type Component} from 'grapesjs';
import type {SelectedPromptOptionValues} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import type {EmailCampaignAiRegenerateImageSource} from './ai-email-campaign-content-library';
import {sleep} from 'ahooks/es/utils/testingHelpers';
import type {UserImageVOResponse} from '@Libraries/user-image-library';
import type {ImageComponentData} from '@Libraries/email-campaigns-library';
import {convertComponentIfNeeded} from '@Libraries/email-campaigns-library';

export enum ImageComponentType {
  IMAGE = 'image',
  BACKGROUND_IMAGE = 'background_image',
  DESIGN = 'design',
}

interface InitiateAiRedesignImageRegenerationArgs {
  prompt: string;
  imageComponentDataList: ImageComponentData[];
  emailContext: string;
  selectedPromptOptionValues: SelectedPromptOptionValues;
  usageSource: EmailCampaignAiRegenerateImageSource;
  contentKey: string;
}

const IMAGE_REGENERATION_POLLING_MAX_DURATION_MS = 180000;
const IMAGE_REGENERATION_POLLING_INTERVAL_MS = 3000;

function getBackgroundImageDimensions(url: string): Promise<{width: number; height: number}> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = (): void => {
      resolve({height: img.naturalHeight, width: img.naturalWidth});
    };
    img.onerror = reject;
    img.src = url;
  });
}

async function getImageComponentData(imageComponent: Component, imageComponentType: ImageComponentType): Promise<ImageComponentData> {
  const imageComponentId = imageComponent.ccid;
  let url;
  let width;
  let height;

  if (imageComponentType === ImageComponentType.IMAGE || imageComponentType === ImageComponentType.DESIGN) {
    url = imageComponent.attributes.src as string;
    const element = imageComponent.getEl() as HTMLImageElement;
    height = element.naturalHeight;
    width = element.naturalWidth;
  } else if (imageComponentType === ImageComponentType.BACKGROUND_IMAGE) {
    const backgroundImage = (imageComponent.getStyle()['background-image'] as string) || '';
    url = backgroundImage
      .replace(/^url\(["']?/, '')
      .replace(/["']?\)$/, '')
      .trim();
    const dimensions = await getBackgroundImageDimensions(url);
    height = dimensions.height;
    width = dimensions.width;
  }

  return {
    ccid: imageComponentId,
    type: imageComponentType,
    url: url,
    height: height,
    width: width,
  };
}

export async function gatherImagesData(
  editor: Editor
): Promise<{imageComponentDataList: ImageComponentData[]; allCcids: string[]; errors: Record<string, string>; allComponents: Component[]}> {
  const images = editor.getWrapper()?.find(`[data-pmw-type="${window.PMW.emails.components.graphic.PMW_IMAGE}"]`) ?? [];
  const backgroundImages =
    editor
      .getWrapper()
      ?.find('[style*="background-image:url"], [style*="background-image: url"]')
      .filter((c) => {
        const bg = (c.getStyle()['background-image'] as string) || '';
        const url = bg.replace(/^url\(["']?/, '').replace(/["']?\)$/, '');
        return url.trim() !== '';
      }) ?? [];
  const designPosters = editor.getWrapper()?.find(`[data-pmw-type="${window.PMW.emails.components.graphic.PMW_DESIGN}"]`) ?? [];
  if (images.length === 0 && backgroundImages.length === 0 && designPosters.length === 0) {
    throw new AiTextPromptBoxError('No Image components found in the template', AiTextErrorTypes.NoImage);
  }

  const getImageDataJobs = [
    ...images.map((c) => ({ccid: c.ccid, promise: getImageComponentData(c, ImageComponentType.IMAGE)})),
    ...backgroundImages.map((c) => ({ccid: c.ccid, promise: getImageComponentData(c, ImageComponentType.BACKGROUND_IMAGE)})),
    ...designPosters.map((c) => ({ccid: c.ccid, promise: getImageComponentData(c, ImageComponentType.DESIGN)})),
  ];

  const allCcids: string[] = [];
  const errors: Record<string, string> = {};
  const imageComponentDataList: ImageComponentData[] = [];

  await Promise.allSettled(
    getImageDataJobs.map(({ccid, promise}) =>
      promise
        .then((data) => {
          imageComponentDataList.push(data);
          allCcids.push(ccid);
        })
        .catch((err: unknown) => {
          errors[ccid] = err instanceof Error ? err.message : String(err);
        })
    )
  );

  return {imageComponentDataList, allCcids, errors, allComponents: [...images, ...backgroundImages, ...designPosters]};
}

export function getEmailContextForImageGeneration(editor: Editor): string {
  const pmwTextType = window.PMW.emails.components.text.PMW_TEXT;
  const pmwTextTypeSearchString = `[data-pmw-type="${pmwTextType}"]`;

  const imageSearchString = `[data-pmw-type="${window.PMW.emails.components.graphic.PMW_IMAGE}"]`;
  const backgroundImageSearchString = '[style*="background-image:url"], [style*="background-image: url"]';
  const designPosterSearchString = `[data-pmw-type="${window.PMW.emails.components.graphic.PMW_DESIGN}"]`;

  const components = editor.getWrapper()?.find(`${imageSearchString}, ${backgroundImageSearchString}, ${designPosterSearchString},  ${pmwTextTypeSearchString}`);
  let emailContext = '';

  if (components) {
    for (const component of components) {
      if (component.attributes.type === pmwTextType) {
        const componentContent = component.getEl()?.innerHTML ?? '';
        if (componentContent === '') {
          continue;
        }
        emailContext += `Text: ${componentContent}, \n`;
      } else {
        emailContext += `Image ID: ${component.ccid}, \n`;
      }
    }
  }

  return emailContext;
}

export async function initiateAiRedesignImageRegeneration(args: InitiateAiRedesignImageRegenerationArgs): Promise<void> {
  try {
    await window.PMW.writeLocal('emailcampaignaicontent/initiateAiRedesignImageRegeneration', args);
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    }
  }
}

async function replaceImage(editor: Editor, ccid: string, image: UserImageVOResponse): Promise<void> {
  const imageComponent = editor.getWrapper()?.find(`#${ccid}`)[0];
  if (!imageComponent) return;

  await editor.runCommand(window.PMW.emails.commands.SET_IMAGE_SRC, {
    component: imageComponent,
    source: image.src,
    extension: image.ext,
    hashedFilename: image.filename,
    showLoadingToast: false,
  });

  editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: false, component: imageComponent});
}

async function replaceBackgroundImage(editor: Editor, ccid: string, image: UserImageVOResponse): Promise<void> {
  const imageComponent = editor.getWrapper()?.find(`#${ccid}`)[0];
  if (!imageComponent) return;

  imageComponent.set(window.PMW.emails.props.MEDIA_TYPE, window.PMW.emails.mediaType.IMAGE);
  imageComponent.set(window.PMW.emails.props.HAS_BACKGROUND_MEDIA, true);

  await editor.runCommand(window.PMW.emails.commands.SET_IMAGE_SRC, {
    component: imageComponent,
    source: image.src,
    extension: image.ext,
    hashedFilename: image.filename,
    isBackground: true,
    showLoadingToast: false,
  });

  editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: false, component: imageComponent});
}

async function replaceDesigns(editor: Editor, ccid: string, image: UserImageVOResponse): Promise<void> {
  const designComponent = editor.getWrapper()?.find(`#${ccid}`)[0];
  if (!designComponent) return;

  editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: false, component: designComponent});
  const imageComponent = convertComponentIfNeeded(
    editor,
    designComponent,
    window.PMW.emails.components.graphic.PMW_DESIGN,
    window.PMW.emails.blocks.ID_EXPLORE_MEDIA,
    false,
    false
  );
  designComponent.remove();

  editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: true, component: imageComponent});
  await editor.runCommand(window.PMW.emails.commands.SET_IMAGE_SRC, {
    component: imageComponent,
    source: image.src,
    extension: image.ext,
    hashedFilename: image.filename,
    showLoadingToast: false,
  });
  editor.runCommand(window.PMW.emails.commands.TOGGLE_COMPONENT_LOADING_OVERLAY, {show: false, component: imageComponent});
}

export async function pollAndReplaceGeneratedImages(
  editor: Editor,
  allCcids: string[],
  contentKey: string
): Promise<{generationErrors: Record<string, string>; unresolvedCcids: string[]}> {
  let pendingCcids = [...allCcids];
  const errors: Record<string, string> = {};

  const replacePromises: Promise<void>[] = [];
  const maxIterations = Math.ceil(IMAGE_REGENERATION_POLLING_MAX_DURATION_MS / IMAGE_REGENERATION_POLLING_INTERVAL_MS);
  let completedIterations = 0;

  while (pendingCcids.length > 0 && completedIterations < maxIterations) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(IMAGE_REGENERATION_POLLING_INTERVAL_MS);
    completedIterations = completedIterations + 1;

    // eslint-disable-next-line no-await-in-loop
    const results = (await window.PMW.readLocal('emailcampaignaicontent/getAiRedesignGeneratedImages', {
      contentKey,
      ccidList: pendingCcids,
    })) as Record<string, {image: UserImageVOResponse; imageType: ImageComponentType} | {error: string}>;

    const resolvedCcids: string[] = [];

    for (const [ccid, result] of Object.entries(results)) {
      resolvedCcids.push(ccid);

      if ('error' in result) {
        errors[ccid] = result.error;
      } else {
        switch (result.imageType) {
          case ImageComponentType.IMAGE:
            replacePromises.push(replaceImage(editor, ccid, result.image));
            break;
          case ImageComponentType.BACKGROUND_IMAGE:
            replacePromises.push(replaceBackgroundImage(editor, ccid, result.image));
            break;
          case ImageComponentType.DESIGN:
            replacePromises.push(replaceDesigns(editor, ccid, result.image));
            break;
        }
      }
    }

    pendingCcids = pendingCcids.filter((id) => !resolvedCcids.includes(id));
  }

  await Promise.allSettled(replacePromises);
  return {generationErrors: errors, unresolvedCcids: pendingCcids};
}

export async function saveEmailCampaignAiRegeneratedImages(
  contentKey: string,
  prompt: string,
  selectedPromptOptionValues: SelectedPromptOptionValues,
  usageSource: EmailCampaignAiRegenerateImageSource
): Promise<void> {
  await window.PMW.writeLocal('emailcampaignaicontent/saveAiRegeneratedImages', {
    contentKey,
    prompt,
    selectedPromptOptionValues,
    usageSource,
  });
}
